import { computed, ref } from "vue";
import pinia from "@/plugins/PiniaPlugin";

import { goFetch } from "@/utils/network-utils";
import { usePermissionsStore } from "@/stores/Permissions";

import type { ApiFetchResponse } from "@tmi/types/api/fetch";
import type { DynamicAdsApiResponse } from "@tmi/types/api/dynamic-ads";
import type { DynamicAd } from "@tmi/types/entities";

import { AdType, SiteLocation } from "@tmi/types/entities";

import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

const ads = ref< Array< DynamicAd > >( [] );
// UX roadmap #82: true until the catalog request settles. An inline slot holds its space meanwhile, so an ad that
// arrives after the page has painted does not push the article down.
const catalogPending = ref< boolean >( true );

// The ad type and site location values are authored per-ad in the CMS, so a typo cannot be ruled
// out. These constants are the authority on what is renderable: anything else is dropped from that
// ad's list and logged. Previously an unrecognised value threw, and because the caller runs inside
// the fetch chain whose .catch only warns, one bad value emptied the entire ad catalog site-wide.
const AD_TYPES: ReadonlyArray< string > = [ AdType.Banner, AdType.InPlace, AdType.Popup, AdType.SideLeft, AdType.SideRight ];
const SITE_LOCATIONS: ReadonlyArray< string > = [ SiteLocation.Article, SiteLocation.Dashboard, SiteLocation.MediaPage ];

function keepKnown( values: Array< string > | undefined, allowed: ReadonlyArray< string >, label: string ) : Array< string > {
    const kept: Array< string > = [];

    ( values ?? [] ).forEach( ( value: string ) => {
        if ( allowed.includes( value ) ) {
            kept.push( value );
        } else {
            logger.warn( `Ignoring unrecognised ${ label } value "${ value }" - expected one of ${ allowed.join( ', ' ) }` );
        }
    } );

    return kept;
}

function mapAdTypeValue( adTypeValues: Array< string > ) : Array< string > {
    return keepKnown( adTypeValues, AD_TYPES, 'ad_types' );
}

function mapSiteLocationCode( siteLocationCodes: Array< string > ) : Array< string > {
    return keepKnown( siteLocationCodes, SITE_LOCATIONS, 'areas_of_site' );
}

export function useAdService() {
    function fetchAds() {
        goFetch< DynamicAdsApiResponse, never >( {
            serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
            // Single unpaged read: `skip` is a PAGE INDEX on this catalog, so 0 is page one and
            // 200 is more dynamic ads than Contentful has ever held.
            endpoint: 'content/dynamic-ad-catalog?skip=0&limit=200',
            verbOverride: 'GET'
        } ).then( ( response: ApiFetchResponse< DynamicAdsApiResponse > ) => {
            if ( null != response?.data?.dynamicAdCatalog ) {
                const catalog = response.data.dynamicAdCatalog.dynamicAds;

                catalog.forEach( ( adResponse ) => ads.value.push( {
                    contentfulId: adResponse.id,
                    title: adResponse.title,
                    body: adResponse.body,

                    priority: adResponse.priority,
                    imageUri: adResponse.image_uri,
                    url: adResponse.url,
                    buttonText: adResponse.button_txt,

                    displayProducts: adResponse.display_shortcodes ?? [],
                    includedProducts: adResponse.included_shortcodes ?? [],
                    excludedProducts: adResponse.excluded_shortcodes ?? [],

                    adTypes: mapAdTypeValue( adResponse.ad_types ),
                    siteLocations: mapSiteLocationCode( adResponse.areas_of_site ),
                    displayDelaySec: adResponse.display_delay_sec
                } ) );
            }
        } ).catch( ( error: any ) => {
            logger.warn( JSON.stringify( error.message ) );
        } ).finally( () => {
            catalogPending.value = false;
        } );
    }

    // Whether an inline slot should hold its space: the catalog is still on its way, to a viewer who can be shown ads
    const awaitingCatalog = computed< boolean >( () => catalogPending.value && !usePermissionsStore( pinia ).isAllianceMember );

    function adsFilteredByProductContextAdTypeAndSiteLocation( filterContext: DynamicAdFilterContext ): Array< DynamicAd > {
        const permissionsStore = usePermissionsStore( pinia );
        // SHORTCUT: remove all ads from anyone who assigned Alliance
        if ( permissionsStore.isAllianceMember ) {
            return [];
        }
        return ads.value
            // Filter out any Included Products of which user is not currently a member
            .filter( ( ad: DynamicAd ) =>
                // If there are no included products, do not filter out the advertisement
                0 === ad.includedProducts.length ||
                // Otherwise, filter out any ads that have an included product to which the user is not subscribed
                permissionsStore.hasAccessBasedOnAnyShortCode( ad.includedProducts )
            )
            // Include the ad if the excluded product filter is empty or,,,
            .filter( ( ad: DynamicAd ) =>
                0 === ad.excludedProducts.length ||
                // the excluded product list does not include ANY of the user's subscribed products
                !permissionsStore.hasAccessBasedOnAnyShortCode( ad.excludedProducts )
            )

            // Include the ad in the response...
            .filter( ( ad: DynamicAd ) => {
                const contextProductCodes = filterContext.productShortCodes?.map((code) => code.toLowerCase()) ?? [];

                // If the ad is not restricted to any specific product contexts (ad.displayProducts is empty),
                // it means this ad is general enough to be shown regardless of the page's specific product association.
                if ( ad.displayProducts.length === 0 ) {
                    return true;
                }
                // If the ad IS restricted to specific product contexts (ad.displayProducts is not empty),
                // BUT the current page/context is NOT product-specific (contextProductCodes is empty or undefined),
                // then this ad (which wants a specific product context) should NOT be shown.
                if ( contextProductCodes.length === 0 ) {
                    return false;
                }
                // At this point:
                // 1. The ad IS restricted to specific product contexts (ad.displayProducts is not empty).
                // 2. The current page/context IS product-specific (contextProductCodes is not empty).
                // The ad should be displayed if there's an overlap between the
                // page's associated products and the ad's specified display products.
                return ad.displayProducts.some(
                    ( adDisplayProductShortCode: string ) => contextProductCodes.includes( adDisplayProductShortCode )
                );
            } )
            // Include the ad if the ad type matches the requested context ad type
            .filter( ( ad: DynamicAd ) => ad.adTypes.some( ( adType: string ) => adType.toLowerCase() === filterContext.adType ) )
            // Include the ad if the site location matches the requested context site location
            .filter( ( ad: DynamicAd ) => ad.siteLocations.some( ( siteLocation: string ) => siteLocation.toLowerCase() === filterContext.siteLocation ) );
    }

    // Fire off the loading event when first initialized
    fetchAds();

    return {
        adsFilteredByProductContextAdTypeAndSiteLocation,
        awaitingCatalog
    };
}

type DynamicAdFilterContext = {
    productShortCodes: string[] | undefined,
    adType: string,
    siteLocation: string
}

export type AdService = ReturnType< typeof useAdService >;